/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Primary action primitive with 8 visual variants and 4 sizes
 *   PROPS:   variant (primary|primary-pill|secondary|trading-up|trading-down|ghost|danger|text)
 *            size (sm|md|lg|subscribe)
 *   STATE:   none
 *   DEPENDS: M-CN (cn utility)
 *   EMITS:   native button events via spread props
 *   ACCESSIBILITY: focus-visible ring (info), min 44px touch target (md/lg), keyboard operable
 *   LINKS:   M-BTN
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-12] Added GRACE MODULE_CONTRACT and semantic blocks — no behavior change
 */

import { forwardRef } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/cn"

// --- VariantDefinitions ---
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-body font-semibold text-sm transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/50 active:scale-[0.98] disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary: "bg-primary text-on-primary hover:bg-primary-active rounded-md h-10 px-6",
        "primary-pill":
          "bg-primary text-on-primary hover:bg-primary-active rounded-pill h-11 px-8",
        secondary:
          "bg-surface-card-dark text-on-dark hover:bg-surface-elevated-dark rounded-md h-10 px-5",
        "trading-up":
          "bg-trading-up text-white hover:bg-trading-up/90 rounded-sm h-8 px-5",
        "trading-down":
          "bg-trading-down text-white hover:bg-trading-down/90 rounded-sm h-8 px-5",
        ghost: "text-muted hover:text-on-dark hover:bg-surface-card-dark rounded-md h-9 px-4",
        danger: "bg-trading-down text-white hover:bg-trading-down/90 rounded-md h-10 px-6",
        text: "text-primary hover:text-primary-active rounded-md h-9 px-3",
      },
      size: {
        sm: "h-8 px-4 text-xs",
        md: "",
        lg: "h-12 px-8 text-base",
        subscribe: "h-7 px-4 text-xs",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
)

// --- TypeDefinitions ---
interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

// --- ButtonComponent ---
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
export type { ButtonProps }
